import { logD, logE } from "@blacknut/logging/dist";
import { FullscreenHelper, FullscreenHelperListener } from "../fullscreen";
import { PeerConnectionCommon } from "./negotiation";
import { HTMLVideoElementWrappper } from "./play";
import { LaunchStreamOpts, UserAgent } from "@blacknut/javascript-sdk/dist";

export declare type NativeTarget = {
  postMessage: typeof window.postMessage;
  addEventListener: typeof window.addEventListener;
  removeEventListener: typeof window.removeEventListener;
};
export interface NativeBridge {
  createPeerConnection(
    config: RTCConfiguration,
    peerId: number,
    streamId: string,
  ): PeerConnectionCommon;
  createHTMLVideoElementWrapper(): HTMLVideoElementWrappper;
  teardown: () => void;
  onStop: () => void;
  onError: (e: { code: string; message: string }) => void;
  onPlay?(): void;
  onCancelLaunch?: () => void;
  onGameLoaded?: () => void;
  createFullScreenHelper?: () => FullscreenHelper;
  onExit?: (callback: () => void) => void;
  getDeviceInfo?: () => Promise<{ userAgent?: UserAgent }>;
  getLaunchStreamOpts?: () => Promise<Partial<LaunchStreamOpts>>;
}

class NativeBridgeFullscreenHelper implements FullscreenHelper {
  private fullscreenListeners: FullscreenHelperListener[] = [];
  private bridge: DefaultNativeBridge;
  constructor(bridge: DefaultNativeBridge) {
    this.bridge = bridge;
  }

  addFullcreenChangeListener(callback) {
    this.fullscreenListeners.push(callback);
  }
  removeFullscreenChangeListener(callback) {
    this.fullscreenListeners = this.fullscreenListeners.filter((c) => c !== callback);
  }

  exitFullscreen() {
    return this.bridge.send<void>("exitFullscreen", undefined);
  }

  isFullscreen() {
    return this.bridge.send<boolean>("isFullscreen", undefined);
  }

  requestFullscreen() {
    return this.bridge.send<void>("requestFullscreen", undefined);
  }

  teardown() {
    //nop
  }

  public onFullscreenChange(fullscreen: boolean) {
    for (const listener of this.fullscreenListeners) {
      listener(fullscreen);
    }
  }
}
export abstract class DefaultNativeBridge implements NativeBridge {
  private callbacks: Record<
    string,
    { resolve: (data: any) => void; reject: (e: any) => void }
  > = {};
  private static id = 0;
  private target: NativeTarget;

  private _onMessage: (e: MessageEvent<any>) => void;
  private loggingTag: string;

  protected exitCallback?: () => void;

  private fullscreenHelper?: NativeBridgeFullscreenHelper;

  private _exitting = false;
  public get exitting(): boolean {
    return this._exitting;
  }
  public set exitting(v: boolean) {
    this._exitting = v;
  }

  constructor(tag: string, target: NativeTarget) {
    this.target = target;
    this.loggingTag = tag;
    this._onMessage = this.onMessage.bind(this);
    this.target.addEventListener("message", this._onMessage);
    this.send<void>("init", undefined);
  }

  public teardown() {
    this.target.removeEventListener("message", this._onMessage);
  }

  protected handleMessage(data: any) {
    switch (data.key) {
      case "fullscreenChange": {
        this.fullscreenHelper?.onFullscreenChange(data.payload);
        return true;
      }
    }
    return false;
  }

  private onMessage(e: MessageEvent<any>) {
    logD(this.loggingTag, "Got message %o", e);
    const s = e.data;
    if (typeof s === "string") {
      try {
        const data = JSON.parse(s);
        if (data.type === `__native_from_native`) {
          const id = data.id;
          const status = data.status;
          const payload = data.payload;
          if (id !== undefined) {
            if (this.callbacks[id]) {
              const p = this.callbacks[id];

              if (status === 0) {
                // OK
                p.resolve(JSON.parse(payload));
              } else {
                // KO
                p.reject(data.message);
              }
              delete this.callbacks[id];
            } else {
              logE(this.loggingTag, "No callback with given id: %o", data);
            }
          } else {
            this.handleMessage(data);
          }
        }
      } catch (e) {
        logE(this.loggingTag, "Caught error handling message: %o", e);
      }
    }
  }

  public close() {
    this.send<void>("close", undefined)
      .then(() => {
        logD(this.loggingTag, "Close ok");
        this.teardown();
      })
      .catch((e) => {
        logE(this.loggingTag, "Caught error on close: %o", e);
        this.teardown();
      });
  }

  public send<T>(key: string, data: any) {
    DefaultNativeBridge.id++;

    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject({ code: -1, message: `Timeout reached waiting for ${key}` });
      }, 10000);

      const message = JSON.stringify({
        type: `__native_from_js`,
        id: DefaultNativeBridge.id,
        key,
        payload: data,
      });
      logD(this.loggingTag, "Sending data to Android: %o", message);
      this.target.postMessage(message);
      this.callbacks[`${DefaultNativeBridge.id}`] = {
        resolve: (data) => {
          clearTimeout(timer);
          resolve(data);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      };
    });
  }

  onStop() {
    // Here we cech for the exiting tag, if Android calls exit, it does not care so much about the onStop event
    // and this prevent having error logged
    if (this.exitting) {
      this.send<void>("onStop", undefined).catch((e) => {
        logD(this.loggingTag, "Caught error calling onStop while exiting: %o", e);
      });
    } else {
      this.send<void>("onStop", undefined).catch((e) => {
        logE(this.loggingTag, "Caught error calling onStop: %o", e);
      });
    }
  }

  onError(e: { code: string; message: string }) {
    this.send<void>("onError", e).catch((e) => {
      logE(this.loggingTag, "Caught error calling onError: %o", e);
    });
  }
  onCancelLaunch() {
    this.send<void>("onCancel", undefined).catch((e) => {
      logE(this.loggingTag, "Caught error calling onCancel: %o", e);
    });
  }

  onExit?(callback: () => void) {
    this.exitCallback = callback;
  }
  abstract createPeerConnection(
    config: RTCConfiguration,
    peerId: number,
    streamId: string,
  ): PeerConnectionCommon;

  abstract createHTMLVideoElementWrapper(): HTMLVideoElementWrappper;

  public createFullScreenHelper(): FullscreenHelper {
    this.fullscreenHelper = new NativeBridgeFullscreenHelper(this);
    return this.fullscreenHelper;
  }

  getDeviceInfo() {
    return this.send<{ userAgent: UserAgent }>("getDeviceInfo", undefined).catch((e) => {
      logE(this.loggingTag, "Caught error calling getDeviceInfo: %o", e);
      return Promise.resolve({ userAgent: undefined });
    });
  }

  getLaunchStreamOpts() {
    return this.send<Partial<LaunchStreamOpts>>("getLaunchStreamOpts", undefined).catch(
      (e) => {
        logE(this.loggingTag, "Caught error calling getLaunchStreamOpts: %o", e);
        return Promise.resolve({});
      },
    );
  }
}
